import { Button, ButtonRow, Icon, PopoverConfirm, Tooltip, usePrevious } from '@timelyapp/react';
import TagFilterDropdown from 'components/dropdowns/TagFilterDropdown';
import FeatureGatedButton from 'components/FeatureGatedButton';
import InlineMessage from 'components/InlineMessage';
import Popover from 'components/popovers/Popover';
import { HeaderRow } from 'components/ui-blocks/header';
import { useWithDispatch } from 'hooks/useWithDispatch';
import React, { useCallback, useEffect } from 'react';
import FontAwesome from 'react-fontawesome';
import { useSelector } from 'react-redux';
import {
  getShowHourStateDropdown,
  getShowUpgradeModalForHourStateDropdown,
  getUserSelectableHourStates,
} from 'redux/modules/hourStates';
import { fetchProject, searchProjects } from 'redux/modules/projects.actions';
import { showSubscriptionNoticeModal } from 'redux/modules/subscriptions.actions';
import { batchDeleteSelection, batchUpdateSelection } from 'redux/modules/superreports.actions';
import { batchSelectionActions } from 'redux/modules/superreports.types';
import { getReportBatchActionsData } from 'redux/selectors/superreports.batch';
import { getHourStateIcon } from 'routes/hourStates/HourStateIcon';
import HourStateDropdown from 'routes/supercalendar/components/calendar/HourStateDropdown';
import { hasLockedEntries } from '~redux/selectors/hours';
import type { IState } from '~redux/state';
import styles from './LoggedEntryBatchActions.module.scss';
import ReportBatchUpdateProject from './ReportBatchUpdateProject';

interface LoggedEntryBatchActionsProps {
  className?: string;
  reportsContext?: boolean;
  hasSelectedAllFiltered?: boolean;
  selectedRowsBillable?: boolean;
}

const LoggedEntryBatchActions: React.FC<LoggedEntryBatchActionsProps> = ({
  className,
  reportsContext,
  hasSelectedAllFiltered,
  selectedRowsBillable = true,
}) => {
  const { clients, currency, projects, tags, selection, states, ui, userId } =
    useSelector(getReportBatchActionsData);
  const showUpgradeModalForHourStateDropdown = useSelector(getShowUpgradeModalForHourStateDropdown);
  const showHourStateDropdown = useSelector(getShowHourStateDropdown);
  const userSelectableStates = useSelector(getUserSelectableHourStates);
  const hasLockedEntriesInSelection = useSelector((state: IState) =>
    hasLockedEntries(state, selection.hourIds)
  );

  const actions = useWithDispatch({
    setSelection: batchSelectionActions.setSelection,
    clearSelection: batchSelectionActions.clearSelection,
    batchUpdateSelection,
    batchDeleteSelection,
    searchProjects,
    fetchProject,
    showSubscriptionNoticeModal,
  });

  const previousUi = usePrevious(ui);

  useEffect(() => {
    if (previousUi.canUpdateProject && !ui.canUpdateProject && selection.projectId) {
      actions.setSelection({ selectionKey: 'projectId', value: null });
    }

    if (previousUi.canUpdateTags && !ui.canUpdateTags && selection.labelIds.length) {
      actions.setSelection({ selectionKey: 'labelIds', value: [] });
    }
  }, [
    actions,
    previousUi.canUpdateProject,
    previousUi.canUpdateTags,
    selection.labelIds.length,
    selection.projectId,
    ui.canUpdateProject,
    ui.canUpdateTags,
  ]);

  const showUpgradeModal = useCallback(() => {
    actions.showSubscriptionNoticeModal('featureHourStatesRequiresUpgrade');
  }, [actions]);

  const handleCancel = () => {
    actions.clearSelection();
  };

  const handleTagBatchChange = (labelIds: Array<number>) => {
    actions.setSelection({
      selectionKey: 'labelIds',
      value: labelIds,
    });
  };

  const handleBatchUpdate = (updateBillable?: boolean, convertPlannedTimeToLogged?: boolean) => {
    actions.batchUpdateSelection({
      reportsContext,
      updateBillable,
      convertPlannedTimeToLogged,
      hasSelectedAllFiltered,
    });
  };

  const handleStateChange = useCallback(
    (stateIds: Array<number> | null) => {
      actions.setSelection({
        selectionKey: 'stateId',
        value: stateIds?.[0]!,
      });
    },
    [actions]
  );

  if (!ui.isVisible) {
    return null;
  }

  const PrimaryButton = ({ onClick }: { onClick?: any }) => {
    const button = (
      <Button
        data-testid="update_hour_state"
        priority="xhigh"
        disabled={ui.updateActionDisabled}
        size="small"
        caret={false}
      >
        {ui.updateLabel}
      </Button>
    );
    const selectedState =
      selection.stateId !== undefined ? userSelectableStates[selection.stateId] : undefined;
    const showConfirmMessage =
      selectedState &&
      !selectedState.locked &&
      hasLockedEntriesInSelection &&
      selection.hourIds.length > 1;
    return showConfirmMessage ? (
      <PopoverConfirm
        onConfirm={e => onClick?.(e)}
        prompt="Are you sure you want to unlock selected entries?"
        target={button}
        confirmLabel="Proceed"
      />
    ) : (
      React.cloneElement(button, { onClick: (e: React.SyntheticEvent) => onClick?.(e) })
    );
  };
  const selectionCounter = (
    <div className={styles.text}>
      <div data-testid="number_of_entries" className={styles.label}>
        <strong>{ui.affectedEntriesLabel}</strong>selected
      </div>
    </div>
  );

  if (ui.allHoursAreReadonly) {
    return (
      <HeaderRow className={className}>
        <ButtonRow>
          {selectionCounter}

          <div className={styles.disabledReason} data-testid="bulk-editing-locked-reaason">
            The selected entries are locked for editing.
          </div>
        </ButtonRow>
      </HeaderRow>
    );
  }

  const renderHourStateDropdown = () => {
    if (showUpgradeModalForHourStateDropdown) {
      return (
        <FeatureGatedButton size="small" isFeatureLocked onClickLocked={showUpgradeModal} caret>
          Update state
        </FeatureGatedButton>
      );
    }
    if (showHourStateDropdown) {
      const hasStateSelected = selection.stateId !== undefined;
      const isNoStateSelected = selection.stateId === 0;

      return (
        <Tooltip tooltip={ui.cannotUpdateStateReason}>
          <HourStateDropdown
            onChange={handleStateChange}
            disableBillable={!selectedRowsBillable}
            filterMode={false}
            showNoState
          >
            <Button
              priority="medium"
              size="small"
              data-testid="report_batch_update_state"
              disabled={!ui.canUpdateState}
              caret
              selected={hasStateSelected}
            >
              {isNoStateSelected ? (
                <>
                  <Icon name="Contrast01" type="solid" />
                  No state
                </>
              ) : hasStateSelected ? (
                <>
                  {getHourStateIcon(states[selection.stateId!])}
                  {states[selection.stateId!].name}
                </>
              ) : (
                <>
                  <Icon name="Contrast01" type="solid" />
                  Update state
                </>
              )}
            </Button>
          </HourStateDropdown>
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <>
      <HeaderRow className={className}>
        <ButtonRow>
          {selectionCounter}
          {renderHourStateDropdown()}
          <Tooltip tooltip={ui.cannotUpdateTagsReason}>
            <TagFilterDropdown
              disableSelectNone
              highlightModified={true}
              emptyLabel="Update tags"
              batchMode
              selected={selection.labelIds}
              tags={tags}
              onChange={handleTagBatchChange}
              singleChildSelect
              clearTooltip="Clear the tags"
              disabled={!ui.canUpdateTags}
              size="small"
            />
          </Tooltip>
          <Tooltip tooltip={ui.cannotUpdateProjectReason}>
            <ReportBatchUpdateProject
              allProjectsLoaded={ui.allProjectsLoaded}
              clients={clients}
              currency={currency}
              isLoading={ui.isLoadingProjects}
              projectId={selection.projectId}
              projects={projects}
              searchProjects={actions.searchProjects}
              setSelection={actions.setSelection}
              userId={userId}
              fetchProject={actions.fetchProject}
              disabled={!ui.canUpdateProject}
              size="small"
            />
          </Tooltip>
          {!ui.isLoadingProjects && ui.userIsNotInProject && (
            <InlineMessage type="error" size="small" style={{ marginBottom: 0 }}>
              <FontAwesome name="ban" style={{ marginRight: 10 }} />
              The author of your selected {selection.hourIds.length === 1 ? 'entry' : 'entries'}{' '}
              isn’t part of that project.
            </InlineMessage>
          )}
          {ui.showBatchLogPlannedTime && (
            <PopoverConfirm
              targetCaret={false}
              target={
                <Button priority="low" style={{ marginLeft: 5 }} size="small">
                  Log planned time
                </Button>
              }
              confirmButtonPriority="high"
              prompt="This will convert all planned time into logged time for the selected entries."
              onConfirm={() => handleBatchUpdate(undefined, true)}
            />
          )}
          {ui.hasProjectBillableMismatch ? (
            // - If ui.selectedProjectBillableState is true, we are moving from a non-billable
            //   project to a billable project, so we give the user the option to update the
            //   billable state of the hours.
            // - If ui.selectedProjectBillableState is false, we are moving from a billable to a
            //   non-billable project. We don't support billable entries in non-billable projects
            //   yet, so these entries must be made non-billable.
            <Popover stopPropagation closeOnClick target={<PrimaryButton />}>
              <div className={styles.updateBillablePrompt}>
                <p>
                  The project you’ve indicated is{' '}
                  {ui.selectedProjectBillableState ? 'billable' : 'non-billable'}, but some of the
                  entries are {!ui.selectedProjectBillableState ? 'billable' : 'non-billable'}.
                </p>
                <p data-testid="billable_prompt">
                  {ui.selectedProjectBillableState
                    ? 'Would you like to mark these entries as billable?'
                    : 'These entries will be automatically marked as non-billable.'}
                </p>
                <div className={styles.buttons}>
                  <Button size="small" priority="xhigh" onClick={() => handleBatchUpdate(true)}>
                    {ui.selectedProjectBillableState ? 'Yes' : 'OK'}
                  </Button>
                  {ui.selectedProjectBillableState && (
                    <Button size="small" onClick={() => handleBatchUpdate(false)}>
                      No
                    </Button>
                  )}
                  <Button size="small">Cancel</Button>
                </div>
              </div>
            </Popover>
          ) : (
            <PrimaryButton onClick={() => handleBatchUpdate(false)} />
          )}
          <Tooltip tooltip={ui.cannotDeleteReason}>
            <PopoverConfirm
              onConfirm={actions.batchDeleteSelection}
              prompt={ui.deleteConfirmMessage}
              target={
                <Button
                  data-testid="delete_entries"
                  caret={false}
                  priority="high"
                  disabled={ui.deleteActionDisabled}
                  destructive
                  size="small"
                >
                  {ui.deleteLabel}
                </Button>
              }
              confirmLabel="Proceed"
            />
          </Tooltip>
          <Button priority="low" onClick={handleCancel} size="small">
            Cancel
          </Button>
        </ButtonRow>
      </HeaderRow>
      {ui.hasReadonlyHours && !ui.allHoursAreReadonly && (
        <HeaderRow>
          <div data-testid="warning_msg" className={styles.readonlyWarning}>
            Some of the selected entries are locked for editing and won't be updated
          </div>
        </HeaderRow>
      )}
    </>
  );
};

export default LoggedEntryBatchActions;
